import type { ReactNode } from 'react';
import { Lock } from 'lucide-react';
import { withAlpha, type PaymentTheme } from '../utils/paymentDisplay';
import { BrandLogo } from './PaymentUi';

interface PaymentHeaderProps {
  theme: PaymentTheme;
  /** Bloc titre affiché sous la barre d'identité (la page fait chevaucher ses cartes dessus). */
  children?: ReactNode;
}

/**
 * Bandeau d'en-tête aux couleurs du garant (ou dégradé Affinicar par défaut) :
 * logo dans un cartouche blanc, nom, mention « Paiement sécurisé ».
 */
export function PaymentHeader({ theme, children }: PaymentHeaderProps) {
  const textColor = theme.primaryTextColor;

  return (
    <header
      className={`tw-relative tw-overflow-hidden ${
        theme.branded ? '' : 'tw-bg-gradient-to-r tw-from-affinicar-primary tw-to-affinicar-secondary'
      }`}
      style={theme.branded ? { backgroundColor: theme.primaryColor, color: textColor } : { color: textColor }}
    >
      {/* Léger relief, quelle que soit la couleur de fond */}
      <div
        aria-hidden="true"
        className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-gradient-to-br tw-from-white/10 tw-via-transparent tw-to-black/10"
      />

      <div className="tw-relative tw-mx-auto tw-max-w-6xl tw-px-4 sm:tw-px-6">
        <div className="tw-flex tw-items-center tw-gap-4 tw-py-4 sm:tw-py-5">
          {(theme.logoUrl || theme.name) && (
            <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">
              {theme.logoUrl && <BrandLogo key={theme.logoUrl} src={theme.logoUrl} alt={theme.name ? '' : 'Logo'} />}
              {theme.name && (
                <span className="tw-truncate tw-text-base tw-font-semibold sm:tw-text-lg">{theme.name}</span>
              )}
            </div>
          )}

          <span
            className="tw-ml-auto tw-inline-flex tw-flex-shrink-0 tw-items-center tw-gap-1.5 tw-rounded-full tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium sm:tw-text-sm"
            style={{
              backgroundColor: withAlpha(textColor, 0.12),
              boxShadow: `inset 0 0 0 1px ${withAlpha(textColor, 0.25)}`,
            }}
          >
            <Lock className="tw-h-3.5 tw-w-3.5" aria-hidden="true" />
            Paiement sécurisé
          </span>
        </div>

        {children && <div className="tw-pb-20 tw-pt-4 sm:tw-pb-24 sm:tw-pt-6">{children}</div>}
      </div>
    </header>
  );
}
